import React, { useState } from 'react'
import { Button, Text, Alert, AlertDescription } from '@flatfile/baseline'
import { EmailAuthForm } from './EmailAuthForm'
import { getGuestAuthToken } from './guestAuth'

// Check if guest auth is enabled by looking for the disabled error message
const isGuestAuthEnabled = () => {
  try {
    // This is a simple way to check - we look at the implementation
    // In the future, this could be an environment variable or config
    return false // Currently disabled based on guestAuth.ts implementation
  } catch {
    return false
  }
}

interface AuthChoiceProps {
  onAuthenticated: () => void
}

export const AuthChoice: React.FC<AuthChoiceProps> = ({ onAuthenticated }) => {
  const [mode, setMode] = useState<'choice' | 'email'>('choice')
  const [isGuestLoading, setIsGuestLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGuestAuth = async () => {
    setIsGuestLoading(true)
    setError(null)

    try {
      // Re-enable guest auth when user explicitly chooses it
      const token = await getGuestAuthToken()
      onAuthenticated()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to authenticate as guest'
      setError(message)
    } finally {
      setIsGuestLoading(false)
    }
  }

  const handleEmailSuccess = () => {
    onAuthenticated()
  }

  const handleEmailError = (error: string) => {
    setError(error)
  }

  if (mode === 'email') {
    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <div className='max-w-md w-full'>
          <Button
            variant='ghost'
            onClick={() => setMode('choice')}
            className='mb-4 text-sm'
          >
            ← Back to options
          </Button>
          <EmailAuthForm
            onSuccess={handleEmailSuccess}
            onError={handleEmailError}
          />
          {error && (
            <Alert variant='destructive' className='mt-4'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen flex items-center justify-center p-4'>
      <div className='max-w-md w-full p-6 bg-base-white rounded-lg shadow-md'>
        <Text variant='title1' as='h1' className='mb-6 text-center'>
          Welcome to Obvious
        </Text>
        <Text
          variant='body'
          className='mb-8 text-center text-muted-foreground block'
        >
          Choose how you'd like to get started
        </Text>

        <div className='space-y-4'>
          {isGuestAuthEnabled() && (
            <>
              <Button
                variant='secondary'
                onClick={handleGuestAuth}
                disabled={isGuestLoading}
                loading={isGuestLoading}
                className='w-full'
                size='lg'
              >
                Continue as Guest
              </Button>

              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-border' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <Text
                    variant='callout'
                    className='px-2 bg-base-white text-muted-foreground'
                  >
                    or
                  </Text>
                </div>
              </div>
            </>
          )}

          <Button onClick={() => setMode('email')} className='w-full' size='lg'>
            Sign In / Sign Up
          </Button>
        </div>

        <div className='mt-6 text-center'>
          {isGuestAuthEnabled() ? (
            <>
              <Text
                variant='callout'
                className='text-muted-foreground block mb-1'
              >
                Guest mode: Start immediately, data saved locally
              </Text>
              <Text variant='callout' className='text-muted-foreground'>
                Account mode: Save your work, sync across devices
              </Text>
            </>
          ) : (
            <Text variant='callout' className='text-muted-foreground'>
              Create an account to save your work and sync across devices
            </Text>
          )}
        </div>

        {error && (
          <Alert variant='destructive' className='mt-4'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}
